import { HttpErrorResponse, HttpEvent, HttpHandlerFn, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { catchError, EMPTY, Observable, throwError } from 'rxjs';
import { environment } from 'src/environments/environment';
import { AuthService } from './auth.service';

// FLAG TO PREVENT MULTIPLE SIMULTANEOUS LOGOUT CALLS WHEN CONCURRENT API REQUESTS RETURN 401.
// NEVER RESET TO FALSE — RESETS NATURALLY ON PAGE RELOAD AFTER REDIRECT TO /login.
let isLoggingOut = false;

export const authInterceptor: HttpInterceptorFn = (
	req: HttpRequest<any>,
	next: HttpHandlerFn
): Observable<HttpEvent<any>> => {
	const authService = inject(AuthService);
	const toaster = inject(ToastrService);

	const logout = () => {
		if (isLoggingOut) return;

		isLoggingOut = true;

		// SHOW TOAST IMMEDIATELY SO USER SEES IT BEFORE THE ASYNC REDIRECT.
		toaster.error('Your session has expired! Please login again.');

		// CLEAR TOKENS SO SUBSEQUENT REQUESTS DON'T CARRY AN EXPIRED TOKEN.
		localStorage.removeItem('token');
		sessionStorage.removeItem('token');

		// FIRE AND FORGET — NO .then() CALLBACK, NO isLoggingOut RESET.
		authService.logout('SECURITY_MEASURE');
	};

	// EMPTY, not throwError, on 401/422/403 — deliberate. 401/422 kicks off an async redirect to
	// /login, and letting the error through would stack "unable to load X" toasts from ~40
	// handlers on top of the session-expired message the user actually needs to read.
	//
	// The cost is that a caller never learns the request failed, so optimistic UI is left
	// showing a change the server refused. Callers must settle their own state in a finalize()
	// rather than in the error handler — see updateStatus in navbar.component.ts.
	const handleAuthError = (error: any) => {
		if (error instanceof HttpErrorResponse && !req.url.includes('/logout')) {
			if (error.status === 401 || error.status === 422) {
				logout();
				return EMPTY;
			}
			if (error.status === 403) {
				toaster.error('You do not have permission to perform this action.');
				return EMPTY;
			}
		}

		return throwError(() => error);
	};

	if (req.url.includes(environment.s3Bucketname)) {
		return next(req);
	} else {
		let idToken = localStorage.getItem('token');

		// BACKUP: Check sessionStorage if localStorage fails
		if (!idToken) {
			idToken = sessionStorage.getItem('token');
		}

		if (idToken) {
			const cloned = req.clone({
				headers: req.headers.set('Authorization', 'Bearer ' + idToken)
			});

			return next(cloned).pipe(catchError(handleAuthError));
		} else {
			return next(req).pipe(catchError(handleAuthError));
		}
	}
};
